import { useField } from "react-final-form";

import { Heading, MessageBar, MessageBarType, Stack } from "@bps/fluent-ui";
import { ClaimDiagnosisDto } from "@libs/gateways/acc/AccGateway.dtos.ts";
import { claimFormNameOf } from "@modules/acc/screens/claim/claim.utils.ts";
import { ClaimFormLabels } from "@modules/acc/screens/claim/components/ClaimFormEnums.ts";
import { ClaimFormValues } from "@shared-types/acc/claim-form-values.type.ts";
import { InjuryDiagnosisAndAssistanceFormValues } from "@shared-types/acc/injury-diagnosis-and-assistance-values.type.ts";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";

import { sideFilter, terminologyFilter } from "../utils.ts";
import { DiagnosisField } from "./DiagnosisField.tsx";
import { DiagnosisHeader } from "./DiagnosisHeader.tsx";

interface PrimaryDiagnosisFormFieldsProps {
  name: string;
  showStatusCol: boolean;
  diagnoses?: ClaimDiagnosisDto[];
  disabled?: boolean;
}

export const PrimaryDiagnosisFormFields: React.FC<
  PrimaryDiagnosisFormFieldsProps
> = props => {
  const {
    input: { value: primaryDiagnosis }
  } = useField(props.name, {
    subscription: { value: true }
  });

  return (
    <>
      <Heading variant="section-heading-light">
        {ClaimFormLabels.primaryDiagnosis}
      </Heading>
      <Fieldset frame styles={{ root: { padding: "0px 0px 8px" } }}>
        <DiagnosisHeader showStatusCol={props.showStatusCol} />
        <Stack
          tokens={{ childrenGap: 8 }}
          styles={{
            root: {
              paddingLeft: 8,
              paddingRight: 8
            }
          }}
        >
          <DiagnosisField
            filters={{
              terminologyFilter: terminologyFilter(
                props.diagnoses
                  ? [...props.diagnoses, primaryDiagnosis]
                  : [primaryDiagnosis],
                primaryDiagnosis
              ),
              sideFilter: sideFilter(
                props.diagnoses
                  ? [...props.diagnoses, primaryDiagnosis]
                  : [primaryDiagnosis],
                primaryDiagnosis
              )
            }}
            name={props.name}
            showStatusCol={props.showStatusCol}
            disabled={props.disabled}
          />
          <FieldSpy<
            InjuryDiagnosisAndAssistanceFormValues["primaryDiagnosis"],
            ClaimFormValues
          >
            name={claimFormNameOf("primaryDiagnosis")}
          >
            {(_state, value) => {
              // We want to display the message only if accAcceptable is equal to false (it can also be undefined).
              if (value?.aCCAcceptable === false) {
                return (
                  <MessageBar
                    messageBarType={MessageBarType.warning}
                    messageBarIconProps={{ iconName: "Error" }}
                  >
                    Non-standard diagnosis; ACC may reject this diagnosis
                  </MessageBar>
                );
              }
              return null;
            }}
          </FieldSpy>
        </Stack>
      </Fieldset>
    </>
  );
};
